import React from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, ClockIcon, ExternalLinkIcon, LanguagesIcon, MapPinIcon, MessageSquareIcon, PhoneIcon } from 'lucide-react';
import { THERAPISTS } from '../data/therapists';
import { LOCATIONS, SERVICE_MENUS } from '../data/locations';
function toIntlNumber(phone: string) {
  const digits = phone.replace(/\D/g, '');
  return digits.startsWith('0') ? `+61${digits.slice(1)}` : digits;
}
export function TherapistDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const therapist = THERAPISTS.find(t => t.slug === slug);
  if (!therapist) {
    return <Navigate to="/therapists" replace data-id="element-368" data-mp="2n5wftdguhxkj" />;
  }
  const studio = LOCATIONS.find(l => l.slug === therapist.locationSlug);
  const others = THERAPISTS.filter(t => t.slug !== therapist.slug).slice(0, 3);
  const smsBody = studio ? `Hi ${studio.name}, I'd like to book a massage with ${therapist.name}. Sent via the Me Time website.` : '';
  return <main className="w-full bg-ink-950" data-id="element-369" data-mp="1eewf2j80bup5">
      <section className="glow-warm w-full bg-ink-950" data-id="element-370" data-mp="19d75z94chr3p">
        <div className="mx-auto w-full max-w-7xl px-5 pt-14 sm:px-8" data-id="element-371" data-mp="19fs7fx0qe50x">
          <Link to="/therapists" className="inline-flex items-center gap-2 text-[11px] font-medium uppercase tracking-[0.24em] text-neutral-400 hover:text-lotus-500" data-id="element-372" data-mp="14lnyj87wnmx7">
            <ArrowLeftIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-373" data-mp="vnvjm563ihac" />
            All ladies
          </Link>
        </div>

        <div className="mx-auto grid w-full max-w-7xl gap-12 px-5 py-14 sm:px-8 lg:grid-cols-[minmax(0,460px)_1fr]" data-id="element-374" data-mp="qpajs6ur9zmf">
          <motion.div initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1]
        }} className="overflow-hidden rounded-2xl border border-white/5" data-id="element-375" data-mp="hmj600eeqrrm">
            <img src={therapist.image} alt={`${therapist.name}, massage therapist`} className="h-full w-full object-cover" data-id="element-376" data-mp="2c1bxbn89cwdr" />
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 18
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.1,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-377" data-mp="e5wscj5p0y51">
            <p className="text-[11px] font-medium uppercase tracking-[0.4em] text-lotus-500" data-id="element-378" data-mp="4f26vh09u2kn">
              {therapist.origin} · {therapist.experience}
            </p>
            <h1 className="mt-5 font-display text-6xl font-light tracking-wide text-white" data-id="element-379" data-mp="x2enlajr3g61">
              {therapist.name}
            </h1>
            <div className="rule-gold mt-6 w-24" data-id="element-380" data-mp="j5syop1v51nr" />
            <p className="text-neon mt-6 font-display text-2xl font-light italic" data-id="element-381" data-mp="o1qbildx3pvs">
              {therapist.tagline}
            </p>
            <p className="mt-6 max-w-xl text-[15px] font-light leading-relaxed text-neutral-400" data-id="element-382" data-mp="33smbp2nllemq">
              {therapist.bio}
            </p>

            <dl className="mt-10 grid gap-6 sm:grid-cols-2" data-id="element-383" data-mp="30bgq8xiy6czu">
              <div data-id="element-384" data-mp="30e2vurma2iff">
                <dt className="text-[10px] font-medium uppercase tracking-[0.28em] text-neutral-500" data-id="element-385" data-mp="2waeoa8xnbmu6">
                  Specialties
                </dt>
                <dd className="mt-3 flex flex-wrap gap-2" data-id="element-386" data-mp="3a9nssbchosib">
                  {therapist.specialties.map(s => <span key={s} className="rounded-full border border-lotus-500/25 px-3 py-1 text-[11px] font-light tracking-wide text-lotus-400" data-id="element-387" data-mp="35bvzbcw74yc0">
                      {s}
                    </span>)}
                </dd>
              </div>
              <div data-id="element-388" data-mp="2mklfffow1ltm">
                <dt className="text-[10px] font-medium uppercase tracking-[0.28em] text-neutral-500" data-id="element-389" data-mp="2mk37vmwrzl6e">
                  Languages
                </dt>
                <dd className="mt-3 flex items-center gap-2 text-sm font-light text-neutral-300" data-id="element-390" data-mp="1yt644xf4vu68">
                  <LanguagesIcon className="h-4 w-4 text-orchid-400" aria-hidden="true" data-id="element-391" data-mp="1tvnyph0brx2z" />
                  {therapist.languages.join(' · ')}
                </dd>
              </div>
            </dl>

            {studio && <div className="mt-10 rounded-2xl border border-white/5 bg-ink-850/80 p-6" data-id="element-392" data-mp="1658w6tllqw2i">
                <p className="text-[10px] font-medium uppercase tracking-[0.28em] text-lotus-500" data-id="element-393" data-mp="163e7d477mjt5">
                  Usually at
                </p>
                <h2 className="mt-3 font-display text-2xl font-light tracking-wide text-white" data-id="element-394" data-mp="1ftz5kqiz1mbj">
                  <Link to={`/locations/${studio.slug}`} className="hover:text-lotus-400" data-id="element-395" data-mp="1kt2amayybsji">
                    {studio.name}
                  </Link>
                </h2>
                <ul className="mt-4 space-y-2.5 text-sm font-light text-neutral-400" data-id="element-396" data-mp="1tujlnexi541c">
                  <li className="flex gap-2.5" data-id="element-397" data-mp="3rkky7qp7ocli">
                    <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0 text-lotus-400" aria-hidden="true" data-id="element-398" data-mp="3rlwcikga92qv" />
                    {studio.address}
                  </li>
                  <li className="flex gap-2.5" data-id="element-399" data-mp="3o4qgxmhx4kq9">
                    <ClockIcon className="mt-0.5 h-4 w-4 shrink-0 text-orchid-400" aria-hidden="true" data-id="element-400" data-mp="3jboyj4ii631x" />
                    {studio.hours}
                  </li>
                </ul>

                <div className="mt-6 flex flex-col gap-3 sm:flex-row" data-id="element-401" data-mp="3ad3iph4f124u">
                  {studio.squareBookingUrl ? <a href={studio.squareBookingUrl} target="_blank" rel="noopener noreferrer" className="glow-pink inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-lotus-500 px-6 py-3.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-lotus-600" data-id="element-402" data-mp="atrdc5ig7ojj">
                      Book with {therapist.name}
                      <ExternalLinkIcon className="h-4 w-4" aria-hidden="true" data-id="element-403" data-mp="2hla39rw6kkop" />
                    </a> : <a href={`sms:${toIntlNumber(studio.phone)}?&body=${encodeURIComponent(smsBody)}`} className="glow-pink inline-flex flex-1 items-center justify-center gap-2 rounded-full bg-lotus-500 px-6 py-3.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-lotus-600" data-id="element-404" data-mp="2j1xki5tmjuxc">
                      <MessageSquareIcon className="h-4 w-4" aria-hidden="true" data-id="element-405" data-mp="25492d5qxz1vz" />
                      Request {therapist.name}
                    </a>}
                  <a href={`tel:${studio.phone.replace(/\s/g, '')}`} className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-white/15 px-6 py-3.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:border-lotus-500 hover:text-lotus-400" data-id="element-406" data-mp="1ve616i9o2hmk">
                    <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-407" data-mp="12ry60n3silkr" />
                    Call location
                  </a>
                </div>
              </div>}
          </motion.div>
        </div>
      </section>

      <section aria-labelledby="menu-heading" className="w-full border-t border-white/5 bg-ink-950" data-id="element-408" data-mp="17qsotp1rca4v">
        <div className="mx-auto w-full max-w-7xl px-5 py-20 sm:px-8" data-id="element-409" data-mp="1lqahifi6c7ji">
          <h2 id="menu-heading" className="font-display text-3xl font-light tracking-wide text-white" data-id="element-410" data-mp="1loh9owvtrh9x">
            Sessions with {therapist.name}
          </h2>
          <div className="mt-8 grid gap-6 sm:grid-cols-2" data-id="element-411" data-mp="1vfkd45c888cd">
            {SERVICE_MENUS.map(menu => <section key={menu.name} aria-label={menu.name} className="rounded-2xl border border-white/5 bg-ink-850/80 p-7" data-id="element-412" data-mp="1qjdcms2lnp1t">
                <h3 className="font-display text-2xl font-light tracking-wide text-white" data-id="element-413" data-mp="1zjrzvwz4je7y">
                  {menu.name}
                </h3>
                <ul className="mt-5 divide-y divide-white/5" data-id="element-414" data-mp="3bszhittzlrbh">
                  {menu.tiers.map(tier => <li key={tier.duration} className="flex items-baseline justify-between gap-4 py-3.5" data-id="element-415" data-mp="3fuughrog3sm3">
                      <span className="text-sm font-light uppercase tracking-[0.16em] text-neutral-300" data-id="element-416" data-mp="2o3ws2zt42u6f">
                        {tier.duration}
                      </span>
                      <span className="font-display text-2xl font-light text-lotus-400" data-id="element-417" data-mp="2s3fanoxgmt63">
                        {tier.price}
                      </span>
                    </li>)}
                </ul>
              </section>)}
          </div>
        </div>
      </section>

      <section aria-labelledby="others-heading" className="glow-warm w-full bg-ink-900" data-id="element-418" data-mp="24duqx3thfrvr">
        <div className="mx-auto w-full max-w-7xl px-5 py-20 sm:px-8" data-id="element-419" data-mp="29d7czmcj4uxa">
          <h2 id="others-heading" className="font-display text-3xl font-light tracking-wide text-white" data-id="element-420" data-mp="21ug97gcksmst">
            You might also like
          </h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-3" data-id="element-421" data-mp="1rwjz8rvdjxwa">
            {others.map(other => <Link key={other.slug} to={`/therapists/${other.slug}`} className="group flex items-center gap-4 rounded-2xl border border-white/5 bg-ink-850/80 p-4 transition-colors hover:border-lotus-500/40" data-id="element-422" data-mp="1nwhvmd62wued">
                <img src={other.image} alt="" aria-hidden="true" className="h-16 w-16 shrink-0 rounded-full object-cover" data-id="element-423" data-mp="196tv3e5q1zrp" />
                <span data-id="element-424" data-mp="105vw5aa54ifz">
                  <span className="block font-display text-xl font-light tracking-wide text-white" data-id="element-425" data-mp="10543o8bub0tx">
                    {other.name}
                  </span>
                  <span className="mt-0.5 block text-[10px] font-medium uppercase tracking-[0.24em] text-lotus-500" data-id="element-426" data-mp="2llrd27b4ecze">
                    {other.origin}
                  </span>
                </span>
                <ArrowRightIcon className="ml-auto h-4 w-4 shrink-0 text-lotus-500 transition-transform group-hover:translate-x-1" aria-hidden="true" data-id="element-427" data-mp="2vbkmp2rpci10" />
              </Link>)}
          </div>
        </div>
      </section>
    </main>;
}